import {
    AccountWithExtraInfo,
    Currency,
    formatAccountSizesBreakdownStat,
    formatAccountTypesBreakdownStat,
    formatPortfolio,
    ModelFractionalSharePolicy,
    ModelPortfolioOptions,
    ModelRtbs,
} from '@orbis-systems/orbis-api-middleware';
import {
    keepPreviousData,
    useMutation,
    UseMutationOptions,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import { useCurrentBaseCurrency, useTranslateFx } from '@/components/~shared/~hooks';
import { selectGlobalSettings, useSelector } from '@/redux';
import { invalidateDirectMidasData } from '../quote';

import { getQueryStates, invalidateCache } from '../utils';
import { userQueries } from '../user';
import { branchQueries } from '../branch/queries';
import {
    AllocationsParams,
    AllocationsResponse,
    ListingResponse,
    ModelComponentUpdateParams,
    ModelComponentUpdateResponse,
    ModelCreationParams,
    ModelCreationResponse,
    ModelDeletionResponse,
    ModelListingQueryParams,
    modelMutations,
    modelQueries,
    ModelRealtimeBalanceHistoryParams,
    ModelRebalanceRecommendationsQueryParams,
    ModelUpdateParams,
    ModelUpdateResponse,
} from './queries';
import {
    formatModelWithRtbsAndBaseCurrency,
    formatModelsWithRtbsAndBaseCurrency,
    parseModelRebalancingThresholdFromJson,
} from './models-query-utils';
import { combineAccountsWithRtbs, combineModelsWithRtbs } from '../branch';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

const modelsListingQueryKey = [...modelQueries.all().queryKey, 'listing'];
const modelRtbsQueryKey = [...modelQueries.all().queryKey, 'memberRealtimeBalances'];

export interface GetModelsListingQueryParams extends ModelListingQueryParams {
    staleTime?: number;
    enabled?: boolean;
}

export const useGetModelsListingQuery = (params: GetModelsListingQueryParams) => {
    const { enabled = true, staleTime, ...restParams } = params;

    return useQuery({
        ...modelQueries.listing(restParams),
        staleTime,
        enabled,
    });
};

export interface GetModelsQueryParams {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
}

export const useGetModelsQuery = (params: GetModelsQueryParams) => {
    const { enabled, staleTime, gcTime } = params;

    return useQueries({
        queries: [
            { ...userQueries.userInformation(), enabled, staleTime, gcTime },
            { ...branchQueries.branchRtbs(), enabled, staleTime, gcTime },
            { ...modelQueries.memberRealtimeBalances({ modelId: 'models' }), enabled, staleTime, gcTime },
            { ...modelQueries.listing({ loadQuotes: true, loadAccounts: true }), enabled, staleTime, gcTime },
        ],
        combine: (results) => {
            const [{ data: userInfo }, { data: branchInfo }, { data: memberRealtimeBalances }, { data: models }] =
                results;

            const accountsWithRtbs = combineAccountsWithRtbs(userInfo?.data.accountList ?? [], branchInfo?.data ?? []);
            const modelsWithRtbs = combineModelsWithRtbs(
                memberRealtimeBalances?.data ?? [],
                models?.data ?? [],
                accountsWithRtbs,
            );

            return {
                data: modelsWithRtbs,
                ...getQueryStates(results),
            };
        },
    });
};

export interface GetModelsWithRtbsQueryParams {
    enabled: boolean;
}

export const useGetModelsWithRtbsQuery = (params: GetModelsQueryParams = {}) => {
    const { enabled, staleTime, gcTime } = params;
    const {
        fxRate,
        baseCurrency: selectedBaseCurrency,
        isLoading: isFxLoading,
        isError: isFxError,
    } = useTranslateFx(Currency.UsDollar, staleTime);
    const baseCurrency = selectedBaseCurrency || Currency.UsDollar;

    const queries = useQueries({
        queries: [
            { ...modelQueries.memberRealtimeBalances({ modelId: 'models' }), enabled, staleTime, gcTime },
            { ...modelQueries.listing({ loadQuotes: true, loadAccounts: true }), enabled, staleTime, gcTime },
            { ...userQueries.middlewareGetPreferences(), enabled, staleTime, gcTime },
        ],
    });
    const [{ data: memberRealtimeBalances }, { data: models }, { data: preferences }] = queries;
    const queryStates = getQueryStates(queries);

    const data = useMemo(
        () =>
            formatModelsWithRtbsAndBaseCurrency({
                models: models?.data ?? [],
                memberRealtimeBalances: memberRealtimeBalances?.data ?? [],
                baseCurrency,
                fxRate,
                thresholdsById: parseModelRebalancingThresholdFromJson(preferences?.data ?? {}),
            }),
        [baseCurrency, fxRate, memberRealtimeBalances?.data, models?.data, preferences?.data],
    );

    return {
        data,
        baseCurrency,
        ...queryStates,
        isLoading: queryStates.isLoading || isFxLoading,
        isError: queryStates.isError || isFxError,
        refetch: () => Promise.all(queries.map((query) => query.refetch())),
    };
};

export const useGetModelPortfolioWithBreakdownQuery = (params: {
    staleTime?: number;
    enabled?: boolean;
    modelId: number;
}) => {
    const forceDirectMidasQuotes = useSelector(selectGlobalSettings).forceDirectMidasQuotes;
    const { modelId, enabled = true, staleTime = Infinity } = params;
    const baseCurrency = useCurrentBaseCurrency() || Currency.UsDollar;
    const queries = useQueries({
        queries: [
            { ...branchQueries.branchPortfolio({ currency: baseCurrency, combined: false }), enabled, staleTime },
            { ...branchQueries.branchRtbs(), enabled, staleTime },
            { ...modelQueries.memberAccounts({ modelId: modelId.toString() }), enabled, staleTime },
        ],
    });
    const [portfolio, rtbs, memberAccounts] = queries;
    const queryStates = getQueryStates(queries);
    const lastUpdatedAt = Math.max(memberAccounts.dataUpdatedAt, portfolio.dataUpdatedAt, rtbs.dataUpdatedAt);
    const { data } = useQuery({
        ...modelQueries.modelPortfolioWithBreakdown({ currency: baseCurrency, modelId, lastUpdatedAt }),
        enabled: enabled && queryStates.isSuccess,
        placeholderData: keepPreviousData,
        retry: false,
        staleTime,
    });
    return {
        data,
        ...queryStates,
        refetch: async () => {
            if (forceDirectMidasQuotes) await invalidateDirectMidasData();
            return Promise.all(queries.map((q) => q.refetch()));
        },
    };
};

export interface GetModelPortfolioQueryParams {
    modelId: number;
    options?: ModelPortfolioOptions;
    enabled?: boolean;
    staleTime?: number;
}

export const useGetModelPortfolioQuery = (params: GetModelPortfolioQueryParams) => {
    const { modelId, options, enabled, staleTime } = params;
    const { combined, symbol, currency, positionType } = options || {};

    return useQueries({
        queries: [
            { ...userQueries.userInformation(), enabled, staleTime },
            { ...branchQueries.branchRtbs(), enabled, staleTime },
            { ...modelQueries.memberRealtimeBalances({ modelId: 'models' }), enabled, staleTime },
            { ...modelQueries.listing({ loadQuotes: true, loadAccounts: true }), enabled, staleTime },
            {
                ...modelQueries.portfolio({
                    modelId: modelId !== 0 ? `${modelId}` : 'All',
                    currency,
                    loadQuotes: true,
                    loadIndustries: true,
                    loadCost: !combined,
                    combined,
                }),
                enabled,
                staleTime,
            },
        ],
        combine: (results) => {
            const [
                { data: userInfo },
                { data: branchInfo },
                { data: memberRealtimeBalances },
                { data: models },
                { data: portfolio },
            ] = results;

            const accountsWithRtbs = combineAccountsWithRtbs(userInfo?.data.accountList ?? [], branchInfo?.data ?? []);
            const modelWithRtb = combineModelsWithRtbs(
                memberRealtimeBalances?.data ?? [],
                models?.data ?? [],
                accountsWithRtbs,
            ).find((model) => model.id === modelId);

            if (!portfolio?.data) {
                return {
                    data: null,
                    ...getQueryStates(results),
                };
            }

            const result = formatPortfolio(portfolio.data, accountsWithRtbs, [], modelWithRtb?.balance || 0, {
                symbol,
                positionType: positionType,
            });

            return {
                data: result,
                ...getQueryStates(results),
            };
        },
    });
};

export interface GetModelMemberAccountStatisticsParams {
    modelId: string;
    enabled: boolean;
    staleTime?: number;
}

export const useGetModelMemberAccountStatisticsQuery = (params: GetModelMemberAccountStatisticsParams) => {
    const { modelId, enabled, staleTime } = params;
    const query = useQuery({ ...modelQueries.memberAccountStatistics({ modelId }), enabled, staleTime });

    const data = useMemo(() => {
        return formatAccountTypesBreakdownStat(query.data?.data ?? []);
    }, [query.data?.data]);

    return {
        ...getQueryStates([query]),
        data,
    };
};

export interface GetModelMemberAccountsParams {
    modelId: number;
    enabled: boolean;
    staleTime?: number;
}
export const useGetModelMemberAccountsQuery = (params: GetModelMemberAccountsParams) => {
    const { modelId, enabled, staleTime } = params;
    const query = useQuery({ ...modelQueries.memberAccounts({ modelId: String(modelId) }), enabled, staleTime });
    return query;
};

export interface GetModelAccountSizesBreakdownStatParams {
    modelId: number;
    enabled: boolean;
    staleTime?: number;
}

export const useGetModelAccountSizesBreakdownStatQuery = (params: GetModelAccountSizesBreakdownStatParams) => {
    const { modelId, enabled, staleTime } = params;
    const modelsQuery = useGetModelsQuery({ enabled, staleTime });
    const query = useQuery({ ...modelQueries.memberRealtimeBalances({ modelId: `${modelId}` }), enabled, staleTime });

    const accounts = useMemo(() => {
        return modelsQuery.data.find((model) => model.id === modelId)?.accounts || [];
    }, [modelId, modelsQuery.data]);

    const rtbs = useMemo(() => {
        return (query.data?.data ?? []) as ModelRtbs[];
    }, [query.data?.data]);

    const accountsWithExtraInfo = useMemo(() => {
        return combineAccountsWithRtbs(accounts, rtbs);
    }, [accounts, rtbs]);

    const data = useMemo(() => {
        return formatAccountSizesBreakdownStat(accountsWithExtraInfo);
    }, [accountsWithExtraInfo]);

    return {
        data,
        ...getQueryStates([modelsQuery, query]),
    };
};

type AllocationsMutationParams = UseMutationOptions<AllocationsResponse, AxiosError, AllocationsParams>;

export const useAllocationsMutation = (options?: AllocationsMutationParams) => {
    return useMutation({
        mutationKey: ['allocations'],
        mutationFn: modelMutations.allocations,
        ...options,
    });
};

export interface UseGetModelQueryParams {
    modelId: number;
    enabled?: boolean;
    staleTime?: number;
}

export const useGetModelQuery = (modelId: number, options?: Omit<UseGetModelQueryParams, 'modelId'>) => {
    const { enabled = true, staleTime } = options || {};
    const {
        fxRate,
        baseCurrency: selectedBaseCurrency,
        isLoading: isFxLoading,
        isError: isFxError,
    } = useTranslateFx(Currency.UsDollar, staleTime);
    const baseCurrency = selectedBaseCurrency || Currency.UsDollar;

    const queries = useQueries({
        queries: [
            { ...modelQueries.memberRealtimeBalances({ modelId: 'models' }), enabled, staleTime },
            { ...modelQueries.listing({ loadQuotes: true, loadAccounts: true }), enabled, staleTime },
            { ...modelQueries.memberRealtimeBalances({ modelId: String(modelId) }), enabled, staleTime },
            { ...userQueries.middlewareGetPreferences(), enabled, staleTime },
        ],
    });
    const queryStates = getQueryStates(queries);
    const [{ data: memberRealtimeBalances }, { data: models }, { data: modelAccountsRtbs }, { data: preferences }] =
        queries;

    const data = useMemo(() => {
        const modelData = models?.data?.find((model) => model.id === modelId);

        if (!modelData) {
            return null;
        }

        const thresholdsById = parseModelRebalancingThresholdFromJson(preferences?.data ?? {});
        const defaultRebalanceThreshold = thresholdsById.get(modelId.toString()) ?? 0;

        return formatModelWithRtbsAndBaseCurrency({
            model: modelData,
            modelRtbs: memberRealtimeBalances?.data?.find((item) => item.modelId === modelId),
            accountRtbs: modelAccountsRtbs?.data ?? [],
            baseCurrency,
            fxRate,
            defaultRebalanceThreshold,
        });
    }, [
        memberRealtimeBalances?.data,
        modelAccountsRtbs?.data,
        modelId,
        models?.data,
        baseCurrency,
        fxRate,
        preferences?.data,
    ]);

    return {
        baseCurrency,
        data,
        ...queryStates,
        isLoading: queryStates.isLoading || isFxLoading,
        isError: queryStates.isError || isFxError,
        refetch: () => Promise.all(queries.map((query) => query.refetch())),
    };
};

type DeleteModelMutationParams = UseMutationOptions<ModelDeletionResponse, AxiosError, number>;

export const useDeleteModelMutation = (options?: DeleteModelMutationParams) => {
    const queryClient = useQueryClient();
    const { onSuccess, ...mutationOptions } = options ?? {};

    return useMutation({
        mutationKey: ['deleteModel'],
        mutationFn: modelMutations.deletion,
        onSuccess: (data, variables, context) => {
            queryClient.setQueriesData<ListingResponse>({ queryKey: modelsListingQueryKey }, (oldData) => {
                if (!oldData?.data) {
                    return oldData;
                }

                return {
                    ...oldData,
                    data: oldData.data.filter((model) => model.id !== variables),
                };
            });

            invalidateCache(queryClient, [modelRtbsQueryKey])();
            onSuccess?.(data, variables, context);
        },
        ...mutationOptions,
    });
};

type CreateModelMutationParams = UseMutationOptions<ModelCreationResponse, AxiosError, ModelCreationParams>;

export const useCreateModelMutation = (options?: CreateModelMutationParams) => {
    const queryClient = useQueryClient();
    const { onSuccess, ...mutationOptions } = options ?? {};

    return useMutation({
        mutationKey: ['createModel'],
        mutationFn: modelMutations.creation,
        onSuccess: (data, variables, context) => {
            const createdModel = data.data;

            if (createdModel) {
                queryClient.setQueriesData<ListingResponse>({ queryKey: modelsListingQueryKey }, (oldData) => {
                    if (!oldData) {
                        return oldData;
                    }

                    const models = oldData.data ?? [];
                    const isModelAlreadyCached = models.some((model) => model.id === createdModel.id);

                    return {
                        ...oldData,
                        data: isModelAlreadyCached
                            ? models.map((model) => (model.id === createdModel.id ? createdModel : model))
                            : [...models, { ...createdModel, accounts: [] }],
                    };
                });
            }

            invalidateCache(queryClient, [modelRtbsQueryKey])();
            onSuccess?.(data, variables, context);
        },
        ...mutationOptions,
    });
};

type UpdateModelMutationParams = UseMutationOptions<ModelUpdateResponse, AxiosError, ModelUpdateParams>;

export const useUpdateModelMutation = (options?: UpdateModelMutationParams) => {
    const queryClient = useQueryClient();
    const { onSuccess, ...mutationOptions } = options ?? {};

    return useMutation({
        mutationKey: ['updateModel'],
        mutationFn: modelMutations.update,
        onSuccess: (data, variables, context) => {
            queryClient.setQueriesData<ListingResponse>({ queryKey: modelsListingQueryKey }, (oldData) => {
                if (!oldData?.data) {
                    return oldData;
                }

                return {
                    ...oldData,
                    data: oldData.data.map((model) => {
                        if (model.id !== variables.id) {
                            return model;
                        }

                        return {
                            ...model,
                            title: variables.title,
                            description: variables.description,
                            reserve: variables.reserve,
                            fractionalSharePolicy: variables.fractionalSharePolicy,
                        };
                    }),
                };
            });
            onSuccess?.(data, variables, context);
        },
        ...mutationOptions,
    });
};

type ModelComponentUpdateMutationParams = UseMutationOptions<
    ModelComponentUpdateResponse,
    AxiosError,
    ModelComponentUpdateParams
>;

export const useModelComponentUpdateMutation = (options?: ModelComponentUpdateMutationParams) => {
    const queryClient = useQueryClient();
    const { onSuccess, ...mutationOptions } = options ?? {};

    return useMutation({
        mutationKey: ['modelComponentUpdate'],
        mutationFn: modelMutations.componentUpdate,
        onSuccess: (data, variables, context) => {
            const updatedSymbol = variables.symbol;
            const updatedPercentage = variables.percentage;

            queryClient.setQueriesData<ListingResponse>({ queryKey: modelsListingQueryKey }, (oldData) => {
                if (!oldData?.data) {
                    return oldData;
                }

                return {
                    ...oldData,
                    data: oldData.data.map((model) => {
                        if (model.id !== variables.model.id) {
                            return model;
                        }

                        const existingComponent = model.components.find((c) => c.symbol === updatedSymbol);

                        if (!existingComponent) {
                            return model;
                        }

                        // If the percentage is not provided or is 0, remove the component
                        if (!updatedPercentage) {
                            return {
                                ...model,
                                components: model.components.filter((c) => c.symbol !== updatedSymbol),
                            };
                        }

                        // Update the component
                        return {
                            ...model,
                            components: model.components.map((c) =>
                                c.symbol === updatedSymbol
                                    ? {
                                          ...c,
                                          percentage: updatedPercentage,
                                      }
                                    : c,
                            ),
                        };
                    }),
                };
            });
            invalidateCache(queryClient, [modelsListingQueryKey])();
            onSuccess?.(data, variables, context);
        },
        ...mutationOptions,
    });
};

export interface GetModelRealtimeBalanceHistoryParams extends ModelRealtimeBalanceHistoryParams {
    enabled: boolean;
    staleTime?: number;
}

export const useGetModelRealtimeBalanceHistoryQuery = (params: GetModelRealtimeBalanceHistoryParams) => {
    const { enabled, staleTime, ...restParams } = params;
    const {
        fxRate,
        baseCurrency: selectedBaseCurrency,
        isBaseLoading: isFxLoading,
        isError: isFxError,
    } = useTranslateFx(Currency.UsDollar, staleTime);
    const baseCurrency = selectedBaseCurrency || Currency.UsDollar;

    const query = useQuery({
        ...modelQueries.realtimeBalanceHistory(restParams),
        enabled,
        staleTime,
        select: (data) => data.data ?? [],
    });

    const data = useMemo(() => {
        return query.data?.map((item) => ({
            ...item,
            balance: item.balance * fxRate,
            baseCurrency,
        }));
    }, [query.data, fxRate, baseCurrency]);

    return {
        ...getQueryStates([query]),
        data,
        baseCurrency,
        isLoading: query.isLoading || isFxLoading,
        isError: query.isError || isFxError,
    };
};

interface UnlinkMemberAccountsParams {
    modelId: number;
    accountIds: number[];
    onAccountUnlinked?: (accountId: number) => void;
}

interface UnlinkMemberAccountsResult {
    unlinkedAccountIds: number[];
}

export const useUnlinkMemberAccountMutation = (
    options?: UseMutationOptions<UnlinkMemberAccountsResult, AxiosError, UnlinkMemberAccountsParams>,
) => {
    const queryClient = useQueryClient();
    const { onSuccess, ...mutationOptions } = options ?? {};

    return useMutation({
        mutationKey: ['unlinkMemberAccount'],
        mutationFn: async ({ modelId, accountIds, onAccountUnlinked }) => {
            const unlinkedAccountIds: number[] = [];

            const unlinkAccount = async (accountId: number) => {
                await modelMutations.unlinkMemberAccount({ modelId, accountId });
                unlinkedAccountIds.push(accountId);
                onAccountUnlinked?.(accountId);
            };

            const unlinkResults = await Promise.allSettled(accountIds.map((accountId) => unlinkAccount(accountId)));

            // Update the cache for accounts that were unlinked successfully
            if (unlinkedAccountIds.length) {
                const unlinkedAccountIdsSet = new Set(unlinkedAccountIds);

                queryClient.setQueriesData<ListingResponse>({ queryKey: modelsListingQueryKey }, (oldData) => {
                    if (!oldData?.data) {
                        return oldData;
                    }

                    return {
                        ...oldData,
                        data: oldData.data.map((model) => {
                            if (model.id !== modelId || !model.accounts) {
                                return model;
                            }

                            return {
                                ...model,
                                accounts: model.accounts.filter(
                                    (account) => !unlinkedAccountIdsSet.has(account.accountId),
                                ),
                            };
                        }),
                    };
                });

                queryClient.invalidateQueries({ queryKey: modelQueries.orphanedAccounts().queryKey });
                queryClient.invalidateQueries({ queryKey: modelRtbsQueryKey, refetchType: 'none' });
            }

            const rejectedResult = unlinkResults.find((result) => result.status === 'rejected');

            if (rejectedResult?.status === 'rejected') {
                throw rejectedResult.reason;
            }

            return { unlinkedAccountIds };
        },
        onSuccess,
        ...mutationOptions,
    });
};

interface LinkMemberAccountsParams {
    modelId: number;
    accounts: AccountWithExtraInfo[];
    onAccountLinked?: (accountId: number) => void;
}

interface LinkMemberAccountsResult {
    linkedAccounts: AccountWithExtraInfo[];
}

export const useLinkMemberAccountsMutation = (
    options?: UseMutationOptions<LinkMemberAccountsResult, AxiosError, LinkMemberAccountsParams>,
) => {
    const queryClient = useQueryClient();
    const { onSuccess, ...mutationOptions } = options ?? {};

    return useMutation({
        mutationKey: ['linkMemberAccount'],
        mutationFn: async ({ modelId, accounts, onAccountLinked }) => {
            const linkedAccounts: AccountWithExtraInfo[] = [];

            const linkAccount = async (account: AccountWithExtraInfo) => {
                await modelMutations.linkMemberAccount({ modelId, accountId: account.accountId });
                linkedAccounts.push(account);
                onAccountLinked?.(account.accountId);
            };

            const linkResults = await Promise.allSettled(accounts.map((account) => linkAccount(account)));

            // Update the cache for accounts that were linked successfully
            if (linkedAccounts.length) {
                queryClient.setQueriesData<ListingResponse>({ queryKey: modelsListingQueryKey }, (oldData) => {
                    if (!oldData?.data) {
                        return oldData;
                    }

                    return {
                        ...oldData,
                        data: oldData.data.map((model) => {
                            if (model.id !== modelId) {
                                return model;
                            }

                            return {
                                ...model,
                                accounts: [...(model.accounts ?? []), ...linkedAccounts],
                            };
                        }),
                    };
                });

                queryClient.invalidateQueries({ queryKey: modelQueries.orphanedAccounts().queryKey });
                queryClient.invalidateQueries({ queryKey: modelRtbsQueryKey, refetchType: 'none' });
            }

            const rejectedResult = linkResults.find((result) => result.status === 'rejected');

            if (rejectedResult?.status === 'rejected') {
                throw rejectedResult.reason;
            }

            return { linkedAccounts };
        },
        onSuccess,
        ...mutationOptions,
    });
};

export interface GetOrphanedAccountsQueryParams {
    enabled?: boolean;
    staleTime?: number;
}

export const useGetOrphanedAccountsQuery = (params: GetOrphanedAccountsQueryParams = {}) => {
    const { enabled = true, staleTime } = params;

    return useQuery({ ...modelQueries.orphanedAccounts(), enabled, staleTime });
};

export interface GetModelRebalanceRecommendationsParams extends ModelRebalanceRecommendationsQueryParams {
    enabled?: boolean;
    staleTime?: number;
}

export function useGetModelRebalanceRecommendations(params: GetModelRebalanceRecommendationsParams) {
    const {
        modelId = 0,
        targets = {},
        accountIds = [],
        fractionalSharePolicy = ModelFractionalSharePolicy.RoundDown,
        enabled = true,
        staleTime,
    } = params;

    return useQuery({
        ...modelQueries.getRebalanceRecommendations({
            modelId,
            targets,
            accountIds,
            fractionalSharePolicy,
        }),
        enabled,
        staleTime,
        select: (data) => data.map((item) => (item.quantity < 0 ? { ...item, quantity: 0, expectedValue: 0 } : item)),
    });
}
